import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  useTimebankBalance,
  useTimebankTransactions,
  type TimebankTransaction,
} from "@/hooks/use-timebank";
import { formatDate, formatHours } from "@/lib/format";
import { timebankStatusLabel, timebankStatusStyle } from "@/lib/timebank-status";
import { cn } from "@/lib/utils";

/**
 * Datoen som faktisk betyr noe for raden.
 *
 * For et uttak er det perioden du tar fri. For en oppsparing er det dagen
 * timene ble opparbeidet, som kan ligge et stykke før søknaden ble sendt —
 * derfor `time_entry.start_date` framfor `created_at` når den finnes.
 */
function radDato(tx: TimebankTransaction): string | null {
  if (tx.kind === "withdrawal") return tx.start_date ?? tx.end_date;
  return tx.time_entry?.start_date ?? tx.created_at;
}

/**
 * VIB-2766: Timebank-fane på ansattprofilen, så en leder kan se saldo og
 * historikk for én ansatt uten å gå via Mine oppgaver, som bare viser den
 * innloggede brukerens egen timebank.
 *
 * Fanen leser, den skriver ikke. Godkjenning, avslag og kansellering bor på
 * Timebank-siden, og å duplisere de handlingene hit ville gitt to steder å
 * vedlikeholde samme beslutning.
 *
 * Tallene kommer fra `useTimebankBalance`, samme beregning som den ansatte
 * selv ser. Det er med vilje: to kilder til saldo er to tall som kan bli
 * uenige, og da stoler ingen på noen av dem.
 */
export function TimebankTab({ userId }: { userId: string }) {
  const { data: balance, isLoading: lasterSaldo } = useTimebankBalance(userId);
  const { data: transactions = [], isLoading: lasterHistorikk } =
    useTimebankTransactions(userId);

  if (lasterSaldo || lasterHistorikk) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center h-32">
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const saldo = balance?.balance ?? 0;
  const pendingDeposit = balance?.pendingDeposit ?? 0;
  const pendingWithdrawal = balance?.pendingWithdrawal ?? 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Saldo</p>
              <p className="font-display text-3xl tabular-nums leading-none mt-1">
                {formatHours(saldo)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Oppsparing til behandling
              </p>
              <p className="text-lg tabular-nums mt-1">{formatHours(pendingDeposit)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Uttak til behandling
              </p>
              <p className="text-lg tabular-nums mt-1">{formatHours(pendingWithdrawal)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Saldoen er godkjent oppsparing minus godkjente uttak. Søknader som venter er holdt
            utenfor, så tallet er det den ansatte faktisk har til rådighet.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="font-display text-lg mb-4">Historikk</h3>
          {transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ingen oppsparing eller uttak registrert.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {transactions.map((tx) => {
                const dato = radDato(tx);
                const erUttak = tx.kind === "withdrawal";
                return (
                  <li key={tx.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {erUttak ? "Uttak" : "Oppsparing"}
                          {dato && (
                            <span className="font-normal text-muted-foreground">
                              {" "}
                              · {formatDate(dato)}
                              {erUttak && tx.end_date && tx.end_date !== tx.start_date && (
                                <> – {formatDate(tx.end_date)}</>
                              )}
                            </span>
                          )}
                        </p>
                        {tx.comment && (
                          <p className="text-xs text-muted-foreground mt-0.5">{tx.comment}</p>
                        )}
                        {tx.status === "rejected" && tx.rejected_reason && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Avslått: {tx.rejected_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-baseline gap-3 shrink-0">
                        <span
                          className={cn(
                            "text-xs px-1.5 py-0.5 rounded-full",
                            timebankStatusStyle(tx.status),
                          )}
                        >
                          {timebankStatusLabel(tx.status)}
                        </span>
                        <span className="text-sm tabular-nums">
                          {erUttak ? "−" : "+"}
                          {formatHours(Number(tx.hours))}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
